import { AssetSearchModal } from '@terminal/components/asset-search-modal/AssetSearchModal';
import { QWSStatusView } from '@terminal/components/qws-status-view/QWSStatusView';
import { WithFooterWrapper } from '@terminal/components/with-footer-wrapper/WithFooterWrapper';
import { WATCHLIST_ID } from '@terminal/config/constants';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { ModalTypes, useModal } from '@terminal/hooks/use-modal/useModal';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useQWSCodelist } from '@terminal/hooks/use-qws-codelist/useQWSCodelist';
import { useAssetsFilterEntityTags } from '@terminal/react-query-hooks/useAssetsFilterEntityTags';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ScenarioAssistantModal } from '@terminal/widgets/scenario/components/scenario-assistant-modal/ScenarioAssistantModal';
import { useScenarioAssistant } from '@terminal/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { ScenarioConditions } from '@terminal/widgets/scenario/components/scenario-conditions/ScenarioConditions';
import { SCENARIO_ASSET_FILTER_KEY } from '@terminal/widgets/scenario/hooks/use-scenario-store/filter-utils';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClearAllButton } from './header/clear-all-button/ClearAllButton';
import { useSetScenarioWorkingList } from './hooks/useSetScenarioWorkingList';
import styles from './Scenario.module.scss';

export const Scenario = () => {
  const { t } = useTranslation([
    'widget',
    'chart',
    'scenario',
    'screener',
    'scenarioCommander',
    'countries',
    'analyze',
    'common',
    'qws',
  ]);

  const { params, deleteQueryParam } = useQueryParams();
  const {
    status: qwsStatus,
    watchlistId: qwsWatchlistId,
    error: qwsError,
  } = useQWSCodelist();
  const { setFilterOptions } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });

  const addConditionModal = useModal(ModalTypes.AddCondition);
  const addAssetsModal = useModal(ModalTypes.AddAssets);

  const [scenarioAssistantConditionId, setScenarioAssistantConditionId] =
    useState<string | undefined>();

  const {
    filterGroups,
    scenarioPageEntityTags,
    scenarioPageSearchResultsLimit,
    setScenarioPageEntityTags,
  } = useScenarioStore(state => ({
    filterGroups: state.filterGroups,
    scenarioPageEntityTags: state.scenarioPageEntityTags,
    scenarioPageSearchResultsLimit: state.scenarioPageSearchResultsLimit,
    setScenarioPageEntityTags: state.setScenarioPageEntityTags,
  }));

  //TODO
  // When redirecting to screener page, the filters from scenario are being applied on screener page
  // Uncomment when fixed bug on redirection to screener page
  // useFiltersQueryParam(useScenarioStore);

  const { data: scenarioFiltersTags } = useAssetsFilterEntityTags(filterGroups);
  const { setWorkingList } = useSetScenarioWorkingList();

  const qwsFilterAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!qwsWatchlistId) {
      qwsFilterAppliedRef.current = null;
      return;
    }

    if (qwsStatus !== 'done') {
      return;
    }

    if (qwsFilterAppliedRef.current === qwsWatchlistId) {
      return;
    }

    qwsFilterAppliedRef.current = qwsWatchlistId;

    setFilterOptions({
      filterKey: SCENARIO_ASSET_FILTER_KEY,
      options: [
        {
          name: t('qws:watchlist.name'),
          key: qwsWatchlistId,
          value: { [WATCHLIST_ID]: qwsWatchlistId },
        },
      ],
    });

    deleteQueryParam(queryKeys.codelist, { replace: true });
  }, [qwsStatus, qwsWatchlistId, setFilterOptions, t, deleteQueryParam]);

  const resetAskedQuestions = useScenarioAssistant(
    state => state.resetAskedQuestions
  );

  useEffect(() => {
    setScenarioPageEntityTags(scenarioFiltersTags);
  }, [JSON.stringify(scenarioFiltersTags)]);

  const changeEditFilter = (conditionId: string) => {
    if (conditionId) {
      setScenarioAssistantConditionId(conditionId);
      addConditionModal.open();
    }
  };

  const handleUpdateItems = (modalTags: string[]) => {
    setWorkingList(modalTags);
  };

  const showQwsStatus =
    qwsStatus === 'loading' ||
    qwsStatus === 'error' ||
    (qwsStatus === 'done' && !!qwsWatchlistId);
  if (showQwsStatus) {
    return (
      <QWSStatusView
        status={qwsStatus}
        watchlistId={qwsWatchlistId}
        error={qwsError}
      />
    );
  }

  return (
    <WithFooterWrapper
      shouldHide={!!params[queryKeys.activeDrawer]}
      trackingMetadata={{ source: POSTHOG_TRACKING_SOURCES.SCENARIO }}
    >
      <div className={styles.root}>
        <div className={styles.headerWrapper}>
          <span className={styles.title}>{t('scenario:scenario')}</span>
          <ClearAllButton />
        </div>
        <ScenarioConditions
          entitiesTags={scenarioPageEntityTags}
          noAssetsButtonLabel={t('scenario:emptyState.addAssets')}
          onEditAssets={() => addAssetsModal.open()}
          onAddConditions={() => {
            resetAskedQuestions();
            addConditionModal.open();
          }}
          onEditAssistantCondition={changeEditFilter}
        />
        {addConditionModal.isOpen && (
          <ScenarioAssistantModal
            editFilterKey={scenarioAssistantConditionId}
            isOpen
            onClose={() => {
              setScenarioAssistantConditionId(undefined);
              addConditionModal.close();
            }}
            resetEditFilterKey={() => {
              setScenarioAssistantConditionId(undefined);
            }}
          />
        )}
        {addAssetsModal.isOpen && (
          <AssetSearchModal
            isOpen
            entityTags={scenarioPageEntityTags}
            pageSearchResultsLimit={scenarioPageSearchResultsLimit}
            onClose={addAssetsModal.close}
            handleUpdateItems={handleUpdateItems}
          />
        )}
      </div>
    </WithFooterWrapper>
  );
};
